import { z } from 'zod';
import {
  floatNumSchema,
  nameSchema,
  objectIdSchema,
  priceSchema,
  quantitySchema,
} from '../../validation';
import {
  imageWithUploadSchema,
  insuranceProviderSchema,
  itemPackingSchema,
  itemTypeSchema,
  temperatureSchema,
  tripLoadTypeSchema,
  tripTypeSchema,
  vehicleTypesSchema,
  weightSchema,
} from '../../common';
import { orderAreaSchema, orderModelSchema } from './order.type.model';

//* load - item
//
// Phase 2 (relations-v2): every field except the optional `_id` and
// `instructions` is now `.optional()` so an Order can be persisted with
// a "skeleton" item — or with no items at all (see {@link orderLoadSchema}
// where the `items[]` array drops its `.min(1)` constraint). Rationale:
// the publish-order flow only requires a vehicle to ship; everything
// else (item type, quantity, packing, weight, cost) is filled in
// optionally and can come from the creator Org's
// `settings.item_default_*` fields or the picked Client's settings.
// Downstream readers (`monday`, `order-export`, `order-waybill`,
// `analytics`) all dereferenced `items[0]` or single fields directly —
// they now MUST guard against `items` being empty / fields being
// undefined. See `docs/relations-v2/06-scenarios.md` § Order-defaults.
export const orderItemSchema = z.object({
  /**
   * this will be coming for items collection saved by the user. If empty, then the user did not save for reuse
   * */
  _id: objectIdSchema.optional(),
  /** name of the item */
  name: nameSchema.optional(),
  /** items weights in km, ton, etc */
  weights: weightSchema.optional(),
  /** quantity of the item such sa 24 pallets/packages/ etc */
  quantity: quantitySchema.min(1).optional(),
  /** the packing type of items such as packages/pallets/etc */
  packing: itemPackingSchema.optional(),
  /** item type  */
  type: itemTypeSchema.optional(),
  /** the total cost of item */
  cost: floatNumSchema.min(0).optional(),
  /** UN number of a hazardous load (e.g. "2912"); Bayan requires it under Dangerous Materials. */
  dangerousCode: z.string().trim().min(1).optional(),
  /** special instructions for the item such as (very sensitive to air, make sure XYZ, etc) */
  instructions: z.string().trim().optional(),
  /** item images */
  images: z.array(imageWithUploadSchema).optional(),
});
export type OrderItem = z.infer<typeof orderItemSchema>;

//* load - job
export const orderJobSchema = z.object({
  /**
   * this is the order id of the parent order object.
   *
   * this happens when we create multiple orders from a job such as when request multiple trucks for the same destination
   */
  jobId: objectIdSchema,

  /** this is to indicate of this order is a parent job or not.
   *
   * a parent job is the first order created when the app user request more than one vehicle quantity.
   */
  isParent: z.boolean(),

  /** the requested quantity of vehicles for the overall job
   * this should be counted only when `isParent` is true.
   *
   * This is because it's job ... the user Want 7 trucks, so the first one of trucks will be the parent and the remaining 6 are not.
   *
   * If vehicleQuantity is 1, then there is no difference
   */
  vehicleQuantity: quantitySchema.min(1),
});
export type orderJob = z.infer<typeof orderJobSchema>;

//* load - insurance
export const loadInsuranceSchema = insuranceProviderSchema
  .pick({
    rate: true,
    name: true,
    itemsCoverageAmount: true,
  })
  .extend({
    /** Rate is the cost of insurance represented as `percentage` of the goods value that we have to pay for the insurance provider.
     *
     * for example, if `rate` is `0.1%` and we have items total cost `200,000 SAR`.
     *
     * then the `cost` is `200 SAR` ... (0.1% * 200,000 SAR = `200 SAR`)
     */
    cost: priceSchema,
  });
export type LoadInsurance = z.infer<typeof loadInsuranceSchema>;

//* load
export const orderLoadSchema = z.object({
  /** a job is a collection orders.
   *
   * we will create multiple orders when the user orders more than one truck in one. `vehicleQuantity > 1`
   *
   * So we need a job to keep track of the total vehicle requested, and job id.
   *
   * **Important Note**
   *
   * The `jobId` will be the same as the first order `_id` created.
   */
  job: orderJobSchema,

  /** load type or category
   * - `goods` = means all the items in `load.items` are goods such as food / plastic / other items
   * - `container` = similar to the goods but loaded in a `container`
   */
  category: tripLoadTypeSchema,
  /** the insurance to cover items total costs.
   *
   * If empty, then it means that this order has no insurance
   */
  insurance: loadInsuranceSchema.optional(),
  /** the total of item costs */
  totalItemsCosts: floatNumSchema.min(0).optional(),
  /** the total weight of items this will always be in `kg` */
  totalItemsWeight: weightSchema,
  /** the model to use ... FTL, PTL, LTL, etc */
  model: orderModelSchema,
  /** the requested temperature by the user */
  temperature: temperatureSchema,
  /** the trip of the load such as `one_way` or `round_trip`
   *
   * `round_trip` means the carrier must return to the original pickup point after unloading items.
   *
   * For example, this case will mainly happen when a customer wants ship a `container` from a port or warehouse to some destination.
   *
   * So a `round_trip` will have a similar flow to the following:
   * 1. the carrier `heads` to the customer location or a specific port requested by a customer
   * 2. the carrier `pickup` the container on his flatbed truck
   * 3. the carrier `heads ` to the drop off location specified by the customer
   * 4. the carrier `unload` the items inside the container on the drop off location
   * 5. the carrier `return` the original pickup location an return the empty container to the customer
   */
  tripType: tripTypeSchema,
  /** the requested type of vehicle by the user.
   *
   * the reason it's an array is because some items can be loaded in different type of vehicles.
   *
   * For example, rise food can be loaded in flatbed & side_curtain trucks.
   *
   * Therefore, any vehicle listed in the vehicles array can load the items in the order
   *
   * Phase 2 (relations-v2): empty `[]` is now legal — an Order can be
   * persisted while the vehicle is still being resolved (e.g. on a
   * draft, or before the role-aware default fires). The publish-order
   * payload builder will fall back to the creator Org's
   * `settings.vehicle_preferred_types[0]` (or the picked Client's, for
   * business customers) at submit time so the resolver still has a
   * vehicle to price against. Readers MUST guard `vehicles[0]` access.
   */
  vehicles: z.array(vehicleTypesSchema).default([]),
  /** the type of area such as domestic, cross_border, etc */
  area: orderAreaSchema,
  /** order items that is being shipped.
   *
   * Phase 2 (relations-v2): no longer requires at least one item — an
   * Order may be created with zero items and have its load detail
   * filled in later (or never, when the receiver only cares about
   * vehicle + lane). When empty, totals (`totalItemsCosts`,
   * `totalItemsWeight`) stay at zero and no insurance is calculated.
   */
  items: z.array(orderItemSchema).default([]),
});
export type OrderLoad = z.infer<typeof orderLoadSchema>;
